/*
 * SabrTrader custom header navigation.
 *
 * The theme ships a hover-based, full-width, transparent mega-menu. This override turns the
 * desktop header dropdowns into conventional CLICK-to-open cards:
 *   - clicking a top-level item that has a submenu opens it (the link no longer navigates);
 *   - the panel is a solid, bordered, shadowed card positioned under the item;
 *   - it stays open on hover-out and closes only on a submenu-item click, an outside click, or Escape.
 *
 * Self-contained: injects its own <style>, drives visibility with a `.nav-open` class, and never
 * touches the theme's header-menu web component. Mobile (drawer) is left completely alone.
 */
(function () {
  var DESKTOP = '(min-width: 750px)';

  var css =
    '@media ' + DESKTOP + ' {' +
    /* Kill the hover reveal + header height expansion; we control opening by click. */
    '  header-component { --full-open-header-height: var(--header-height) !important; --submenu-height: 0px !important; }' +
    "  header-menu .menu-list__list-item:where(:not([slot='overflow'])) > .menu-list__submenu { visibility: hidden !important; }" +
    /* Click-opened dropdown */
    '  header-menu .menu-list__list-item.nav-open > .menu-list__submenu {' +
    '    visibility: visible !important; clip-path: none !important; top: 100% !important; --submenu-content-opacity: 1 !important; }' +
    '  header-menu .menu-list__list-item.nav-open > .menu-list__submenu > .menu-list__submenu-inner {' +
    '    opacity: 1 !important; transform: none !important; }' +
    /* Card styling for the panel */
    '  header-menu .menu-list__submenu > .menu-list__submenu-inner {' +
    '    background: #141925; border: 1px solid #2f384b; border-radius: 12px;' +
    '    box-shadow: 0 24px 60px -20px rgba(0,0,0,.85);' +
    '    width: max-content !important; max-width: min(92vw, 420px);' +
    '    padding: 8px !important; margin-block-start: 8px; margin-inline-start: var(--nav-x, 0px);' +
    '    flex-direction: column; justify-content: flex-start; align-items: stretch;' +
    '    gap: 0; row-gap: 0; max-height: min(70vh, 620px); }' +
    /* Collapse the 6-column mega-menu grid into a single vertical list of links. */
    '  header-menu .menu-list__submenu-inner .mega-menu__list {' +
    '    display: flex !important; flex-direction: column !important;' +
    '    grid-template-columns: none !important; gap: 2px !important; width: 100% !important; }' +
    '  header-menu .menu-list__submenu-inner .mega-menu__column {' +
    '    width: auto !important; grid-column: auto !important; margin: 0 !important; padding: 0 !important; }' +
    '  header-menu .menu-list__submenu-inner .mega-menu__column > .list-unstyled {' +
    '    column-count: 1 !important; }' +
    /* Each link on one line, styled as a dropdown row with a hover highlight. */
    '  header-menu .menu-list__submenu-inner .mega-menu__link,' +
    '  header-menu .menu-list__submenu-inner .wrap-text {' +
    '    white-space: nowrap !important; }' +
    '  header-menu .menu-list__submenu-inner .mega-menu__link {' +
    '    display: block !important; width: 100%; padding: 8px 12px !important;' +
    '    border-radius: 8px; color: #d7deea !important; }' +
    '  header-menu .menu-list__submenu-inner .mega-menu__link:hover {' +
    '    background: rgba(255,255,255,.06) !important; color: #fff !important; }' +
    /* Give the top-level link a pointer cursor and a subtle open-state cue */
    '  header-menu .menu-list__list-item:has(> .menu-list__submenu) > .menu-list__link { cursor: pointer; }' +
    '}';

  var style = document.createElement('style');
  style.id = 'sabr-custom-nav';
  style.textContent = css;
  document.head.appendChild(style);

  function isDesktop() {
    return window.matchMedia(DESKTOP).matches;
  }

  function topItems() {
    return document.querySelectorAll("header-menu .menu-list__list-item:not([slot='overflow'])");
  }

  function submenuInner(item) {
    return item && item.querySelector(':scope > .menu-list__submenu .menu-list__submenu-inner');
  }

  function closeAll(except) {
    topItems().forEach(function (it) {
      if (it !== except) it.classList.remove('nav-open');
    });
  }

  function position(item) {
    var submenu = item.querySelector(':scope > .menu-list__submenu');
    if (!submenu) return;
    var iRect = item.getBoundingClientRect();
    var sRect = submenu.getBoundingClientRect();
    submenu.style.setProperty('--nav-x', Math.max(0, Math.round(iRect.left - sRect.left)) + 'px');
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!isDesktop()) return; // mobile drawer: untouched
      var target = event.target;
      if (!(target instanceof Element)) return;

      // A click on a link INSIDE an open submenu: let it navigate, then close.
      var leaf = target.closest('.menu-list__submenu a[href]');
      if (leaf) {
        closeAll();
        return;
      }

      // A click on a top-level menu item.
      var link = target.closest(
        "header-menu .menu-list__list-item:not([slot='overflow']) > .menu-list__link"
      );
      var item = link && link.closest('.menu-list__list-item');
      if (item) {
        if (submenuInner(item)) {
          // Has a dropdown -> open it instead of navigating.
          event.preventDefault();
          event.stopPropagation();
          var willOpen = !item.classList.contains('nav-open');
          closeAll(item);
          if (willOpen) {
            position(item);
            item.classList.add('nav-open');
          } else {
            item.classList.remove('nav-open');
          }
        } else {
          // Plain link -> navigate normally, close any open panel.
          closeAll();
        }
        return;
      }

      // Anything else -> outside click, close.
      closeAll();
    },
    true // capture: preempt the link's default navigation
  );

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAll();
  });

  window.addEventListener('resize', function () {
    var open = document.querySelector('.menu-list__list-item.nav-open');
    if (open) position(open);
  });
})();
